import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';

/**
 * `Modal` is an overlay container that is used as backdrop for modal content, such as dialogs, lightboxes and popup menus.
 *
 * The container will call the *onClose* callback when the user clicks on the modal overlay or hits the Escape key. It will
 * also hijack all mouse wheel events to prevent scrolling of the underlying components while the modal is visible.
 *
 * Note that this component does not have a default styling for the overlay or the layout of its children as this may
 * vary greatly depending on the context in which the `Modal` is used. E.g. a {@link PopupMenu} should not have a colored
 * overlay and the menu should be placed close to the mouse pointer or trigger button, while a dialog should be centered and
 * have a colored overlay.
 *
 * You may use {@link TransitionGroup} as parent container for your modal to add animated effects.
 *
 * **Note**: The child container of this component must have position set to absolute or fixed in order to be rendered on top
 * of the modal overlay.
 *
 * @property {node} children The children that will be displayed on top of the modal overlay.
 * @property {string} className The class name of this modal. Will be appended to "core-modal".
 * @property {boolean} closeOnClickOutside If set to `true`, the *onClose* callback will be called when the user clicks
 * outside the lightbox boundaries.
 * @property {function} onClose A callback that is called whenever the modal is closed either by clicking on the overlay or
 * hitting the Escape key.
 *
 * @see Dialog, Lightbox, TabbedLightbox, TransitionGroup
 */
export default class Modal extends Component {

  static displayName = 'Modal';

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    closeOnClickOutside: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
  };

  static defaultProps = {
    closeOnClickOutside: true
  };

  static onScroll(e) {
    e.preventDefault();
  }

  componentDidMount() {
    window.addEventListener('keydown', this.onKeyDown, false);
    this.refOverlay?.addEventListener('mousewheel', Modal.onScroll, false);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.onKeyDown, false);
    this.refOverlay?.removeEventListener('mousewheel', Modal.onScroll, false);
  }

  onClick = e => {
    const { closeOnClickOutside, onClose } = this.props;

    if (e.target === this.refOverlay && closeOnClickOutside && onClose) {
      e.preventDefault();
      e.stopPropagation();
      onClose(e);
    }
  };

  onKeyDown = e => {
    const { onClose } = this.props;

    if (e.keyCode === 27) {
      onClose(e);
    }
  };

  render() {
    const { children, className, closeOnClickOutside, onClose, ...rest } = this.props; // eslint-disable-line no-unused-vars

    return (
      <div className={cx('core-modal', className)} {...rest}>
        <div className="core-modal-overlay" ref={c => { this.refOverlay = c; }} onClick={this.onClick} data-testid="modal"/>
        <div className="core-modal-content">
          {children}
        </div>
      </div>
    );
  }

}
